'use client';

import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Star, Trash2, User, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  createEmptyCharacter,
  MangaCharacterDraft,
  MAX_MANGA_CHARACTERS,
} from './types';
import { trackCreate } from '@/utils/create-flow-analytics';
import type { CreateFlow } from '@/utils/analytics-events';
import { inferReaderBand } from '@/utils/manga/comic-story-profile';
import {
  chipGender,
  clearHeadCovering,
  headCoveringChipText,
  headCoveringRemoveLabel,
  pickedGender,
} from './head-covering-chip';

interface MangaCharactersStepProps {
  characters: MangaCharacterDraft[];
  onCharactersChange: (characters: MangaCharacterDraft[]) => void;
  onBack: () => void;
  onNext: () => void;
  /** Which create flow this step belongs to (analytics + copy). */
  flow?: CreateFlow;
}

/** Copy per flow. The comic strings are the original wording; do not change them. */
const COPY = {
  comic: {
    title: 'Your Heroes',
    intro:
      'Tell us about each person in the photo. Pick one main hero — the story revolves around them.',
    missingName: 'Please give every hero a name.',
    pickMainTitle: 'Pick a main hero',
    pickMain: 'Choose who the story is mainly about.',
    noun: 'Hero',
    mainBadge: 'Main hero',
    remove: 'Remove hero',
    relationship: 'Relationship to the main hero',
    makeMain: 'Make main hero',
    add: 'Add another hero',
    next: 'Continue to Style',
  },
  'picture-book': {
    title: "Who's in your book",
    intro:
      'Tell us about each person in the photo. Pick who the story is mainly about — it revolves around them.',
    missingName: 'Please give every person a name.',
    pickMainTitle: 'Pick who the story is mainly about',
    pickMain: 'Choose who the story is mainly about.',
    noun: 'Person',
    mainBadge: 'Main person',
    remove: 'Remove person',
    relationship: 'Relationship to the main person',
    makeMain: 'Make main person',
    add: 'Add another person',
    next: 'Continue',
  },
} as const;

const GENDER_OPTIONS = [
  { value: 'boy', label: 'Boy' },
  { value: 'girl', label: 'Girl' },
  { value: 'man', label: 'Man' },
  { value: 'woman', label: 'Woman' },
];

export function MangaCharactersStep({
  characters,
  onCharactersChange,
  onBack,
  onNext,
  flow = 'comic',
}: MangaCharactersStepProps) {
  const t = COPY[flow];
  const { toast } = useToast();

  const updateCharacter = (id: string, patch: Partial<MangaCharacterDraft>) => {
    onCharactersChange(
      characters.map((c) => (c.id === id ? { ...c, ...patch } : c))
    );
  };

  const setMainCharacter = (id: string) => {
    onCharactersChange(
      characters.map((c) => ({ ...c, isMainCharacter: c.id === id }))
    );
  };

  const addCharacter = () => {
    if (characters.length >= MAX_MANGA_CHARACTERS) return;
    onCharactersChange([...characters, createEmptyCharacter(characters.length === 0)]);
  };

  const removeCharacter = (id: string) => {
    if (characters.length <= 1) return;
    const remaining = characters.filter((c) => c.id !== id);
    // Keep exactly one main character
    if (!remaining.some((c) => c.isMainCharacter)) {
      remaining[0] = { ...remaining[0], isMainCharacter: true };
    }
    onCharactersChange(remaining);
  };

  // The covering is only ever taken from the request, so clearing it here keeps it off the book
  const removeHeadCovering = (id: string) => {
    onCharactersChange(clearHeadCovering(characters, id));
    trackCreate(flow, 'head_covering_removed');
  };

  const handleNext = () => {
    if (characters.some((c) => !c.name.trim())) {
      toast({
        title: 'Missing name',
        description: t.missingName,
        variant: 'destructive',
      });
      return;
    }
    if (!characters.some((c) => c.isMainCharacter)) {
      toast({
        title: t.pickMainTitle,
        description: t.pickMain,
        variant: 'destructive',
      });
      return;
    }
    trackCreate(flow, 'people_confirmed', { count: characters.length, band: inferReaderBand(characters) });
    onNext();
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold">{t.title}</h2>
        <p className="text-gray-600 max-w-xl mx-auto">{t.intro}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {characters.map((character, index) => (
          <Card
            key={character.id}
            className={`transition-all border-2 ${
              character.isMainCharacter ? 'border-purple-400 shadow-md' : 'border-transparent'
            }`}
          >
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {character.thumbnail ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={character.thumbnail}
                      alt={`${t.noun} ${index + 1}`}
                      className="w-12 h-12 rounded-full object-cover border border-gray-200"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                      <User className="w-6 h-6 text-purple-600" />
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-gray-800">
                      {character.name.trim() || `${t.noun} ${index + 1}`}
                    </p>
                    {character.isMainCharacter && (
                      <span className="inline-flex items-center text-xs font-medium text-purple-700 bg-purple-100 rounded-full px-2 py-0.5">
                        <Star className="w-3 h-3 mr-1" />
                        {t.mainBadge}
                      </span>
                    )}
                  </div>
                </div>
                {characters.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeCharacter(character.id)}
                    className="text-gray-400 hover:text-red-500 transition-colors p-1"
                    title={t.remove}
                    aria-label={t.remove}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-1">
                  <Label htmlFor={`name-${character.id}`}>Name</Label>
                  <Input
                    id={`name-${character.id}`}
                    value={character.name}
                    placeholder="e.g. Mia"
                    onChange={(e) => updateCharacter(character.id, { name: e.target.value })}
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor={`age-${character.id}`}>Age</Label>
                  <Input
                    id={`age-${character.id}`}
                    type="number"
                    // A newborn is 0 in a picture book (new-sibling); comics keep min 1
                    min={flow === 'picture-book' ? 0 : 1}
                    max={99}
                    value={character.age}
                    onChange={(e) => updateCharacter(character.id, { age: e.target.value })}
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor={`gender-${character.id}`}>Gender</Label>
                  <select
                    id={`gender-${character.id}`}
                    value={character.gender}
                    onChange={(e) => updateCharacter(character.id, pickedGender(e.target.value))}
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    {GENDER_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {!character.isMainCharacter && (
                  <div className="col-span-2 space-y-1">
                    <Label htmlFor={`relationship-${character.id}`}>
                      {t.relationship}
                    </Label>
                    <Input
                      id={`relationship-${character.id}`}
                      value={character.relationship}
                      placeholder="e.g. sister, dad, best friend"
                      onChange={(e) =>
                        updateCharacter(character.id, { relationship: e.target.value })
                      }
                    />
                  </div>
                )}
              </div>

              {character.headCovering && (
                <div
                  data-head-covering=""
                  className="inline-flex max-w-full items-center gap-2 rounded-2xl border border-line bg-paper-raised pl-3 text-sm text-ink"
                >
                  <span className="min-w-0 break-words py-2">
                    {headCoveringChipText(chipGender(character), character.headCovering)}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeHeadCovering(character.id)}
                    aria-label={headCoveringRemoveLabel(character.headCovering)}
                    title={headCoveringRemoveLabel(character.headCovering)}
                    className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-full text-gray-500 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-2"
                  >
                    <X className="w-4 h-4" aria-hidden="true" />
                  </button>
                </div>
              )}

              {!character.isMainCharacter && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => setMainCharacter(character.id)}
                >
                  <Star className="w-4 h-4 mr-2" />
                  {t.makeMain}
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {characters.length < MAX_MANGA_CHARACTERS && (
        <Button type="button" variant="outline" onClick={addCharacter} className="w-full sm:w-auto">
          <Plus className="w-4 h-4 mr-2" />
          {t.add} ({characters.length}/{MAX_MANGA_CHARACTERS})
        </Button>
      )}

      <div className="flex flex-col-reverse sm:flex-row justify-between gap-3">
        <Button variant="outline" size="lg" onClick={onBack} className="w-full sm:w-auto">
          Back
        </Button>
        <Button
          size="lg"
          className="bg-purple-600 hover:bg-purple-700 text-white w-full sm:w-auto"
          onClick={handleNext}
        >
          {t.next}
        </Button>
      </div>
    </div>
  );
}
